import { axiosAzureInstance } from '../Utils/axios';
import type { AxiosResponse } from 'axios';

const BASE_URL = '/uaa/api/observability/analytics';

export interface ClientUsage {
  clientId: string;
  requestCount: number;
  firstSeen: string;
  lastSeen: string;
}

export interface ApiUsage {
  httpMethod: string;
  endpoint: string;
  used: boolean;
  requestCount: number;
  lastUsed: string | null;
  avgDurationMs: number | null;
  errorCount: number;
}

export interface ApiEvent {
  id: number;
  timestamp: string;
  httpStatus: number | null;
  durationMs: number | null;
  errorMessage: string | null;
  principal: string | null;
  endpoint: string | null;
  queryString: string | null;
  userAgent: string | null;
  remoteAddress: string | null;
}

export interface HeatmapCell {
  endpoint: string;
  /** ISO-8601: 1 = Monday .. 7 = Sunday */
  dayOfWeek: number;
  /** 0-23, UTC */
  hour: number;
  requestCount: number;
}

export interface PageParams {
  page?: number;
  size?: number;
}

export interface UsageParams extends PageParams {
  used?: boolean;
  from?: string | null;
  to?: string | null;
  sort?: string | null;
}

export interface RangeParams {
  from?: string | null;
  to?: string | null;
}

/**
 * Encodes an endpoint route pattern (e.g. /api/ip-rights/{id}) for use as the path tail of the
 * event drill-down URL: braces are percent-encoded so they survive to cspservice, path
 * separators are kept intact.
 */
function encodeRoutePattern(pattern: string): string {
  return pattern.replace(/\{/g, '%7B').replace(/\}/g, '%7D');
}

const ApiAnalyticsService = {
  getClients(params: PageParams = {}): Promise<AxiosResponse<ClientUsage[]>> {
    return axiosAzureInstance.get(`${BASE_URL}/clients`, { params });
  },

  getClientApis(clientId: string, params: UsageParams = {}): Promise<AxiosResponse<ApiUsage[]>> {
    return axiosAzureInstance.get(`${BASE_URL}/clients/${encodeURIComponent(clientId)}/apis`, {
      params,
    });
  },

  getClientApiEvents(
    clientId: string,
    httpMethod: string,
    endpointPattern: string,
    params: UsageParams = {}
  ): Promise<AxiosResponse<ApiEvent[]>> {
    const url =
      `${BASE_URL}/clients/${encodeURIComponent(clientId)}/apis/` +
      `${encodeURIComponent(httpMethod)}${encodeRoutePattern(endpointPattern)}/events`;
    return axiosAzureInstance.get(url, { params });
  },

  getClientHeatmap(
    clientId: string,
    params: RangeParams & { endpoint?: string | null } = {}
  ): Promise<AxiosResponse<HeatmapCell[]>> {
    return axiosAzureInstance.get(`${BASE_URL}/clients/${encodeURIComponent(clientId)}/heatmap`, {
      params,
    });
  },

  /** Downloads the xlsx export (Used APIs / Not Used APIs / Events worksheets). */
  exportClientAnalytics(
    clientId: string,
    params: RangeParams = {}
  ): Promise<AxiosResponse<ArrayBuffer>> {
    return axiosAzureInstance.get(`${BASE_URL}/clients/${encodeURIComponent(clientId)}/export`, {
      params,
      responseType: 'arraybuffer',
    });
  },
};

export default ApiAnalyticsService;
